import { useEffect, useMemo, useRef, useState } from 'react';

import { Check, LoaderCircle, Pencil, Search, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/services/utils';

export type ManageableTag = { id: string; name: string; cardCount?: number };

type SortMode = 'mostUsed' | 'az' | 'unused';

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: 'mostUsed', label: 'Most used' },
  { value: 'az', label: 'A–Z' },
  { value: 'unused', label: 'Unused' },
];

export function ManageTagsDialog({
  open,
  tags,
  onOpenChange,
  onRename,
  onDelete,
}: {
  open: boolean;
  tags: ManageableTag[];
  onOpenChange: (open: boolean) => void;
  onRename: (tagId: string, name: string) => void;
  onDelete: (tagIds: string[]) => Promise<boolean>;
}) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortMode>('mostUsed');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [busyIds, setBusyIds] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSort('mostUsed');
      setSelectedIds([]);
      setEditingId(null);
      setEditDraft('');
      setPendingDeleteId(null);
      setPendingBulkDelete(false);
    }
  }, [open]);

  useEffect(() => {
    editInputRef.current?.focus();
    editInputRef.current?.select();
  }, [editingId]);

  const totalCardCount = useMemo(
    () => tags.reduce((sum, tag) => sum + (tag.cardCount ?? 0), 0),
    [tags]
  );

  const selectedCardCount = useMemo(
    () => tags.reduce((sum, tag) => (selectedIds.includes(tag.id) ? sum + (tag.cardCount ?? 0) : sum), 0),
    [tags, selectedIds]
  );

  const visibleTags = useMemo(() => {
    const trimmedQuery = query.trim().toLowerCase();
    const filtered = trimmedQuery
      ? tags.filter((tag) => tag.name.toLowerCase().includes(trimmedQuery))
      : tags;

    if (sort === 'unused') {
      return filtered
        .filter((tag) => (tag.cardCount ?? 0) === 0)
        .sort((left, right) => left.name.localeCompare(right.name));
    }

    if (sort === 'az') {
      return [...filtered].sort((left, right) => left.name.localeCompare(right.name));
    }

    return [...filtered].sort((left, right) => {
      const byCount = (right.cardCount ?? 0) - (left.cardCount ?? 0);
      return byCount !== 0 ? byCount : left.name.localeCompare(right.name);
    });
  }, [tags, query, sort]);

  const busy = (tagId: string) => busyIds.includes(tagId);

  const toggleSelected = (tagId: string) => {
    setSelectedIds((current) =>
      current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId]
    );
  };

  const startEditing = (tag: ManageableTag) => {
    setPendingDeleteId(null);
    setEditingId(tag.id);
    setEditDraft(tag.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const commitEditing = () => {
    const name = editDraft.trim();
    if (editingId && name) {
      onRename(editingId, name);
    }
    cancelEditing();
  };

  const deleteTags = async (tagIds: string[]) => {
    setBusyIds((current) => [...current, ...tagIds]);
    try {
      const success = await onDelete(tagIds);
      if (success) {
        setSelectedIds((current) => current.filter((id) => !tagIds.includes(id)));
      }
    } finally {
      setBusyIds((current) => current.filter((id) => !tagIds.includes(id)));
    }
  };

  const confirmRowDelete = async (tagId: string) => {
    setPendingDeleteId(null);
    await deleteTags([tagId]);
  };

  const confirmBulkDelete = async () => {
    setBulkBusy(true);
    setPendingBulkDelete(false);
    try {
      await deleteTags(selectedIds);
    } finally {
      setBulkBusy(false);
    }
  };

  const closeDisabled = bulkBusy || busyIds.length > 0;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !closeDisabled && onOpenChange(nextOpen)}>
      <DialogContent
        showCloseButton={!closeDisabled}
        className="flex max-h-[85vh] w-full max-w-[700px] flex-col gap-0 overflow-hidden rounded-2xl border-white bg-white p-0"
      >
        <div className="flex flex-col gap-4 border-b border-[#f1eee8] px-6 pb-4 pt-5">
          <div className="flex items-baseline gap-3">
            <DialogTitle className="text-xl font-extrabold text-[#1e1c18]">Manage tags</DialogTitle>
            <span className="text-[13px] text-[#8a8578]">
              {tags.length} {tags.length === 1 ? 'tag' : 'tags'} · {totalCardCount} cards
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative min-w-[180px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-[15px] -translate-y-1/2 text-[#8a8578]" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search tags"
                aria-label="Search tags"
                className="h-[38px] w-full rounded-[10px] bg-[#f3f1ec] pl-9 pr-3 text-[13.5px] text-[#1e1c18] outline-none placeholder:text-[#8a8578] focus:ring-2 focus:ring-[#2f5c4e]/20"
              />
            </div>
            <div className="flex flex-none gap-1 rounded-[10px] bg-[#f3f1ec] p-[3px]" role="tablist" aria-label="Sort tags">
              {SORT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="tab"
                  aria-selected={sort === option.value}
                  onClick={() => setSort(option.value)}
                  className={cn(
                    'flex h-8 items-center rounded-[8px] px-3.5 text-[13px] font-semibold transition',
                    sort === option.value
                      ? 'bg-white text-[#1e1c18] shadow-[0_1px_2px_rgba(0,0,0,.06)]'
                      : 'text-[#57534a] hover:text-[#1e1c18]'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {visibleTags.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-[#8a8578]">
              {query.trim() ? `No tags match "${query.trim()}".` : 'No tags yet.'}
            </div>
          ) : (
            visibleTags.map((tag) => {
              const selected = selectedIds.includes(tag.id);
              const editing = editingId === tag.id;
              const confirmingDelete = pendingDeleteId === tag.id;
              const cardCount = tag.cardCount ?? 0;
              const isBusy = busy(tag.id);

              if (confirmingDelete) {
                return (
                  <div
                    key={tag.id}
                    className="flex flex-wrap items-center gap-3 border-b border-[#f6f4ef] bg-[#fdf7f5] px-6 py-3"
                  >
                    <span className="text-[13px] leading-snug text-[#3d3a33]">
                      Delete <b className="text-[#12141a]">{tag.name}</b>?{' '}
                      {cardCount > 0
                        ? `It'll be removed from ${cardCount} ${cardCount === 1 ? 'card' : 'cards'} — the cards themselves stay.`
                        : 'No cards use this tag.'}
                    </span>
                    <div className="ml-auto flex flex-none gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={isBusy}
                        onClick={() => void confirmRowDelete(tag.id)}
                        className="h-8 rounded-lg bg-[#a8402a] px-3 text-[12.5px] font-bold text-white hover:bg-[#8f3522]"
                      >
                        {isBusy && <LoaderCircle className="size-3.5 animate-spin" />}
                        Delete
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isBusy}
                        onClick={() => setPendingDeleteId(null)}
                        className="h-8 rounded-lg border-[#e2ded5] bg-white px-3 text-[12.5px] font-bold text-[#3d3a33] hover:bg-[#f6f4ef]"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={tag.id}
                  className={cn(
                    'group flex items-center gap-3.5 border-b border-[#f6f4ef] px-6 py-[13px] transition-colors',
                    selected && 'bg-[#f4f8f6]'
                  )}
                >
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={selected}
                    aria-label={`Select ${tag.name}`}
                    onClick={() => toggleSelected(tag.id)}
                    className={cn(
                      'flex size-[18px] flex-none items-center justify-center rounded-[5px] border transition',
                      selected ? 'border-transparent bg-[#2f5c4e]' : 'border-[#cfc9bb] hover:border-[#a39c8d]'
                    )}
                  >
                    {selected && <Check className="size-[11px] text-white" strokeWidth={3.2} />}
                  </button>

                  {editing ? (
                    <input
                      ref={editInputRef}
                      value={editDraft}
                      maxLength={50}
                      aria-label={`Rename ${tag.name}`}
                      onChange={(event) => setEditDraft(event.target.value)}
                      onBlur={commitEditing}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          commitEditing();
                        } else if (event.key === 'Escape') {
                          event.preventDefault();
                          cancelEditing();
                        }
                      }}
                      className="h-8 min-w-0 flex-1 rounded-lg border border-[#2f5c4e] bg-white px-2.5 text-[14.5px] font-bold text-[#1e1c18] outline-none ring-[3px] ring-[#2f5c4e]/15"
                    />
                  ) : (
                    <span className="min-w-0 flex-1 truncate text-[14.5px] font-bold text-[#1e1c18]">{tag.name}</span>
                  )}

                  {!editing && (
                    <span className="flex flex-none items-center gap-2 text-[13px] text-[#8a8578]">
                      {cardCount} {cardCount === 1 ? 'card' : 'cards'}
                    </span>
                  )}

                  <div className="flex w-[66px] flex-none justify-end gap-1">
                    {!editing && (
                      <>
                        <button
                          type="button"
                          aria-label={`Rename ${tag.name}`}
                          disabled={isBusy}
                          onClick={() => startEditing(tag)}
                          className="flex size-[30px] items-center justify-center rounded-lg border border-[#e2ded5] bg-white text-[#57534a] opacity-0 transition hover:bg-[#f6f4ef] focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-40"
                        >
                          <Pencil className="size-[14px]" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Delete ${tag.name}`}
                          disabled={isBusy}
                          onClick={() => setPendingDeleteId(tag.id)}
                          className="flex size-[30px] items-center justify-center rounded-lg border border-[#f0dcd6] bg-white text-[#a8402a] opacity-0 transition hover:bg-[#fdf1ee] focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-40"
                        >
                          {isBusy ? <LoaderCircle className="size-[14px] animate-spin" /> : <Trash2 className="size-[14px]" />}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {selectedIds.length > 0 && !pendingDeleteId && (
          pendingBulkDelete ? (
            <div className="flex flex-wrap items-center gap-3 border-t border-[#f0dcd6] bg-[#fdf7f5] px-6 py-3">
              <span className="text-[13px] leading-snug text-[#3d3a33]">
                Delete <b className="text-[#12141a]">{selectedIds.length} {selectedIds.length === 1 ? 'tag' : 'tags'}</b>?{' '}
                {selectedCardCount > 0
                  ? `${selectedIds.length === 1 ? "It'll" : "They'll"} be removed from ${selectedCardCount} ${selectedCardCount === 1 ? 'card' : 'cards'} — the cards themselves stay.`
                  : `No cards use ${selectedIds.length === 1 ? 'this tag' : 'these tags'}.`}
              </span>
              <div className="ml-auto flex flex-none gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={bulkBusy}
                  onClick={() => void confirmBulkDelete()}
                  className="h-8 rounded-lg bg-[#a8402a] px-3 text-[12.5px] font-bold text-white hover:bg-[#8f3522]"
                >
                  {bulkBusy && <LoaderCircle className="size-3.5 animate-spin" />}
                  Delete
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={bulkBusy}
                  onClick={() => setPendingBulkDelete(false)}
                  className="h-8 rounded-lg border-[#e2ded5] bg-white px-3 text-[12.5px] font-bold text-[#3d3a33] hover:bg-[#f6f4ef]"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3.5 border-t border-[#ece9e2] bg-[#f6f4ef] px-6 py-3.5">
              <span className="text-[13.5px] font-bold text-[#1e1c18]">
                {selectedIds.length} {selectedIds.length === 1 ? 'tag' : 'tags'} selected
              </span>
              <div className="ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPendingBulkDelete(true)}
                  className="h-[34px] rounded-[9px] border-[#f0dcd6] bg-white px-3.5 text-[13px] font-bold text-[#a8402a] hover:bg-[#fdf1ee]"
                >
                  Delete {selectedIds.length === 1 ? 'tag' : `${selectedIds.length} tags`}
                </Button>
              </div>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
